import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import { MessageCircle, Send, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  MetaDashboardVisual, ApiSetupVisual, AppSecretVisual, WebhookVisual,
} from "@/components/WhatsAppWizardVisuals";
import WhatsAppPostSetupChecklist from "@/components/WhatsAppPostSetupChecklist";

const POLL_MS = 10000;

export default function WhatsAppInbox() {
  const [account, setAccount] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activePeer, setActivePeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadAccount = async () => {
    const r = await api.get("/whatsapp/account");
    setAccount(r.data);
    if (!r.data?.configured) setWizardOpen(true);
  };
  const loadConversations = async () => {
    try {
      const r = await api.get("/whatsapp/conversations");
      setConversations(r.data || []);
    } catch {}
  };
  const loadMessages = async (peer) => {
    if (!peer) return;
    try {
      const r = await api.get(`/whatsapp/messages/${peer}`);
      setMessages(r.data || []);
      await api.post(`/whatsapp/mark-read/${peer}`);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    } catch {}
  };

  useEffect(() => { loadAccount(); loadConversations(); }, []);
  useEffect(() => {
    const id = setInterval(() => {
      loadConversations();
      if (activePeer) loadMessages(activePeer);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [activePeer]);
  useEffect(() => { if (activePeer) loadMessages(activePeer); }, [activePeer]);

  const send = async () => {
    if (!draft.trim() || !activePeer || sending) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send", { to: activePeer, type: "text", body: draft });
      setDraft("");
      await loadMessages(activePeer);
      await loadConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const active = conversations.find((c) => c.peer === activePeer);

  return (
    <div className="flex h-[calc(100vh-64px)] lg:h-screen">
      {/* Conversation list */}
      <aside className="w-full sm:w-[320px] border-r co-hairline bg-white flex flex-col shrink-0">
        <div className="px-4 py-4 border-b co-hairline flex items-center justify-between">
          <div>
            <div className="co-label">WhatsApp</div>
            <h1 className="font-heading text-xl font-bold tracking-tight mt-0.5">Inbox</h1>
          </div>
          <button
            onClick={() => setWizardOpen(true)}
            className="text-xs font-semibold text-[#4338CA] hover:text-[#3730A3]"
          >
            {account?.configured ? "Manage" : "Setup"}
          </button>
        </div>
        <WhatsAppPostSetupChecklist
          workspaceId={account?.workspace_id}
          account={account}
          configured={!!account?.configured}
          onOpenWizard={() => setWizardOpen(true)}
        />
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-sm text-[#52525B]">
              {account?.configured
                ? "No conversations yet. Client replies will appear here in real time (polled every 10s)."
                : "Connect your WhatsApp Business API to start receiving messages."}
            </div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.peer}
                data-testid={T.waConversationItem}
                onClick={() => setActivePeer(c.peer)}
                className={`w-full text-left px-4 py-3 border-b co-hairline hover:bg-[#FAFAFA] ${
                  activePeer === c.peer ? "bg-[#F4F4F5]" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-[#0A0A0B] truncate">
                    {c.client?.name || `+${c.peer}`}
                  </span>
                  {c.unread > 0 && (
                    <span className="bg-[#4338CA] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                      {c.unread}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#52525B] truncate mt-0.5">
                  {c.last_direction === "outbound" ? "You: " : ""}
                  {c.last_message}
                </div>
                {c.client && (
                  <div className="text-[10px] text-[#71717A] mt-1">
                    {c.client.product_name} · ₹{c.client.amount} due {c.client.next_due_date}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Chat pane */}
      <main className="flex-1 flex flex-col bg-[#FAFAFA] min-w-0">
        {!activePeer ? (
          <div className="flex-1 grid place-items-center text-[#52525B] text-sm px-6 text-center">
            <div>
              <MessageCircle className="h-10 w-10 mx-auto mb-3 text-[#A1A1AA]" strokeWidth={1.5} />
              Pick a conversation to start chatting.
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 py-3 border-b co-hairline bg-white">
              <div className="font-heading font-bold text-sm text-[#0A0A0B]">
                {active?.client?.name || `+${activePeer}`}
              </div>
              {active?.client && (
                <div className="text-[11px] text-[#52525B]">
                  {active.client.product_name} · ₹{active.client.amount} · due {active.client.next_due_date} ·{" "}
                  <span className="capitalize">{active.client.payment_status}</span>
                </div>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
              {messages.map((m) => (
                <div
                  key={m.wamid}
                  className={`max-w-[75%] px-3 py-2 rounded-sm text-sm ${
                    m.direction === "outbound"
                      ? "ml-auto bg-[#DCF8C6]"
                      : "mr-auto bg-white border co-hairline"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className="text-[10px] text-[#52525B] mt-1 text-right">
                    {new Date((m.timestamp || 0) * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t co-hairline bg-white px-4 py-3 flex items-end gap-2">
              <Textarea
                data-testid={T.waMessageInput}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a message…"
                rows={2}
                className="flex-1 resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button
                data-testid={T.waSendBtn}
                onClick={send}
                disabled={sending || !draft.trim()}
                className="co-btn-press inline-flex items-center gap-1.5 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm disabled:opacity-60"
              >
                <Send className="h-4 w-4" strokeWidth={2} />
                {sending ? "…" : "Send"}
              </button>
            </div>
          </>
        )}
      </main>

      <WhatsAppSetupWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={loadAccount}
        account={account}
      />
    </div>
  );
}

function WhatsAppSetupWizard({ open, onClose, onSaved, account }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    access_token: "", phone_number_id: "", business_account_id: "",
    app_secret: "", display_phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => { if (open) { setStep(1); setTestResult(null); } }, [open]);

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setTestResult(null);
    try {
      await api.post("/whatsapp/regenerate-verify-token");
      await onSaved();
      toast.success("New verify token generated — copy it into Meta.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate");
    } finally { setRegenerating(false); }
  };

  const selfTest = async () => {
    if (testing) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.get("/whatsapp/self-test-webhook");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, reason: e?.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/whatsapp/account", form);
      toast.success("WhatsApp connected");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const copyToken = () => {
    navigator.clipboard?.writeText(account?.verify_token || "");
    toast.success("Verify token copied");
  };
  const copyUrl = () => {
    navigator.clipboard?.writeText(account?.webhook_url || "");
    toast.success("Webhook URL copied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect WhatsApp Business — step {step} of 4</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "First, let's set up a free WhatsApp app on Meta. Follow the steps below."
              : "One-time setup. Copy each value from Meta Business Manager into the field below."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3 py-2 text-sm text-[#3F3F46]">
            <MetaDashboardVisual />
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm p-3 text-xs text-[#166534]">
              <div className="font-bold mb-1">Before you begin — you need:</div>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>A personal Facebook account (any active account works).</li>
                <li>A phone number that is <b>not currently used</b> on WhatsApp (personal or Business app). You'll receive an OTP on it.</li>
                <li>5–10 minutes. This is a one-time setup.</li>
              </ul>
            </div>

            <div className="border co-hairline rounded-sm p-3 text-xs">
              <div className="font-bold text-[#0A0A0B] mb-2">Step-by-step: Create your free WhatsApp app on Meta</div>
              <ol className="list-decimal ml-4 space-y-2 text-[#3F3F46]">
                <li>
                  Open{" "}
                  <a className="text-[#4338CA] underline inline-flex items-center gap-1"
                     target="_blank" rel="noopener noreferrer"
                     href="https://business.facebook.com/">
                    business.facebook.com <ExternalLink className="h-3 w-3" strokeWidth={2} />
                  </a>{" "}
                  and log in with your Facebook account. Create a <b>Business Portfolio</b> if you don't have one (give it your business name, add your email).
                </li>
                <li>
                  Open{" "}
                  <a className="text-[#4338CA] underline inline-flex items-center gap-1"
                     target="_blank" rel="noopener noreferrer"
                     href="https://developers.facebook.com/apps/">
                    developers.facebook.com/apps <ExternalLink className="h-3 w-3" strokeWidth={2} />
                  </a>{" "}
                  and click <b>Create app</b>.
                </li>
                <li>
                  Use case: pick <b>"Other"</b> → app type: <b>"Business"</b> → give the app any name (e.g. <i>"CollectOne WhatsApp"</i>) → link the Business Portfolio you created → <b>Create app</b>.
                </li>
                <li>
                  On the app dashboard, find the <b>WhatsApp</b> product tile and click <b>Set up</b>. This adds WhatsApp to your app for free.
                </li>
                <li>
                  In the left sidebar you'll now see <b>WhatsApp → API Setup</b>. Click it. This is the screen where you'll copy 3 values in the next step.
                </li>
                <li>
                  On the API Setup page, click <b>"Add phone number"</b> and register your business phone number (you'll get an OTP). Once verified, your business number appears in the "From" dropdown.
                </li>
              </ol>
            </div>

            <div className="bg-[#FEF3E7] border border-[#FDE68A] rounded-sm p-3 text-xs text-[#78350F]">
              <b>Tip:</b> Keep the Meta <b>WhatsApp → API Setup</b> tab open in another window. You'll copy 3 values from it in the next step.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            <ApiSetupVisual />
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm p-3 text-xs text-[#166534]">
              <div className="font-bold mb-1">Where to find these on Meta's screen:</div>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Go to your app at <a className="underline" target="_blank" rel="noopener noreferrer" href="https://developers.facebook.com/apps/">developers.facebook.com/apps</a> → left sidebar <b>WhatsApp → API Setup</b>.</li>
                <li>Under <b>"Send and receive messages"</b> you'll see a box titled <b>"Step 1: Select phone numbers"</b>. That box shows all 3 values below.</li>
              </ol>
            </div>

            <div>
              <Label className="co-label">1. Access token</Label>
              <div className="text-[11px] text-[#52525B] mt-1 mb-2">
                On Meta's page: at the top of the box, click the blue <b>"Generate access token"</b> button. A long string starting with <b>EAA…</b> appears — click <b>Copy</b> and paste it here. <span className="text-[#B45309]">Treat this like a password.</span>
              </div>
              <Input data-testid={T.waTokenInput} value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                className="mt-1 font-mono text-xs" placeholder="EAAXXXX..." />
            </div>

            <div>
              <Label className="co-label">2. Phone number ID</Label>
              <div className="text-[11px] text-[#52525B] mt-1 mb-2">
                On Meta's page: the row labelled <b>"Phone number ID"</b> — a number like <span className="font-mono">106540...</span> Click the copy icon next to it and paste here.
              </div>
              <Input data-testid={T.waPhoneIdInput} value={form.phone_number_id}
                onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                className="mt-1 font-mono" placeholder="10654035..." />
            </div>

            <div>
              <Label className="co-label">3. WhatsApp Business Account ID</Label>
              <div className="text-[11px] text-[#52525B] mt-1 mb-2">
                On Meta's page: the row labelled <b>"WhatsApp Business Account ID"</b> — a number like <span className="font-mono">102290...</span> Click the copy icon next to it and paste here.
              </div>
              <Input data-testid={T.waWabaIdInput} value={form.business_account_id}
                onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
                className="mt-1 font-mono" placeholder="10229012..." />
            </div>

            <div>
              <Label className="co-label">Display phone (optional)</Label>
              <Input value={form.display_phone_number}
                onChange={(e) => setForm({ ...form, display_phone_number: e.target.value })}
                className="mt-2" placeholder="+91 98765 43210" />
              <div className="text-[11px] text-[#52525B] mt-1">The number your clients will see — shown in the "From" dropdown on Meta's page.</div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-2">
            <AppSecretVisual />
            <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-sm p-3 text-xs text-[#1E3A8A]">
              <div className="font-bold mb-1">Optional — App secret for webhook security</div>
              Meta signs each incoming webhook with your app&apos;s secret. Paste it here so we can
              verify signatures. If left blank, we accept messages but log a warning.
              <div className="mt-2">Find it at: Meta App → <b>App settings → Basic → App Secret → Show</b>.</div>
            </div>
            <div>
              <Label className="co-label">App secret (optional)</Label>
              <Input data-testid={T.waAppSecretInput} value={form.app_secret}
                onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
                className="mt-2 font-mono" placeholder="Paste here or leave blank" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 py-2">
            <WebhookVisual />
            <div className="bg-[#FEF3E7] border border-[#FDE68A] rounded-sm p-3 text-xs text-[#78350F]">
              <div className="font-bold mb-1">Paste these two values into Meta</div>
              Go to <b>WhatsApp → Configuration → Webhook → Edit</b> and paste these.
              Subscribe to the <b>messages</b> field.
            </div>
            <div>
              <Label className="co-label">Callback URL</Label>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={account?.webhook_url || ""} className="font-mono text-xs" />
                <button onClick={copyUrl} className="px-3 border co-hairline rounded-sm hover:bg-[#F4F4F5]">
                  <Copy className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            </div>
            <div>
              <Label className="co-label">Verify token</Label>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={account?.verify_token || ""} className="font-mono text-xs" />
                <button onClick={copyToken} className="px-3 border co-hairline rounded-sm hover:bg-[#F4F4F5]">
                  <Copy className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
              <div className="text-[11px] text-[#52525B] mt-1">
                Auto-generated for your workspace. Only Meta and CollectOne share it.
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  data-testid={T.waRegenerateBtn}
                  onClick={regenerate}
                  disabled={regenerating}
                  className="text-[11px] font-semibold px-2.5 py-1 border co-hairline rounded-sm hover:bg-[#F4F4F5] disabled:opacity-60"
                >
                  {regenerating ? "Regenerating…" : "Regenerate token"}
                </button>
                <button
                  data-testid={T.waSelfTestBtn}
                  onClick={selfTest}
                  disabled={testing}
                  className="text-[11px] font-semibold px-2.5 py-1 border co-hairline rounded-sm hover:bg-[#F4F4F5] disabled:opacity-60"
                >
                  {testing ? "Testing…" : "Test webhook from CollectOne"}
                </button>
              </div>
              {testResult && (
                <div className={`text-[11px] mt-2 ${testResult.ok ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                  {testResult.ok
                    ? "✓ Webhook + verify token are wired correctly. Now paste into Meta."
                    : `✗ ${testResult.reason}`}
                </div>
              )}
            </div>
            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-sm p-3 text-[11px] text-[#991B1B]">
              <div className="font-bold mb-1">If Meta shows "callback URL or verify token couldn't be validated":</div>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Use the <b>Copy</b> icon here — don't type by hand (invisible spaces cause failures).</li>
                <li>Make sure the domain in the URL is exactly <b>collectone.in</b>. No trailing slash.</li>
                <li>If it still fails, click <b>Regenerate token</b> above, copy the new one, and try again in Meta.</li>
              </ul>
            </div>
            <div className="text-xs text-[#15803D] flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} /> After Meta confirms, incoming messages will arrive here.
            </div>
          </div>
        )}

        <DialogFooter className="justify-between">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm border co-hairline rounded-sm">Back</button>
          ) : <span />}
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)}
              className="px-4 py-2 text-sm font-semibold bg-[#4338CA] text-white rounded-sm co-btn-press">
              Next
            </button>
          ) : (
            <button data-testid={T.waWizardSave} onClick={save} disabled={saving}
              className="px-4 py-2 text-sm font-semibold bg-[#4338CA] text-white rounded-sm co-btn-press disabled:opacity-60">
              {saving ? "Saving…" : "Save & finish"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
